import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, CheckCircle2, Loader2, Signpost, Facebook, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { api } from '../lib/api';
import { PageHeader } from '../components/Shared';
import { FlyerDownloadSection } from '../components/FlyerDownload';
import { FacebookReviewsButton } from '../components/ReviewBlocks';
import { toast } from 'sonner';
import { FACEBOOK_PAGE_URL } from '../lib/social';

// Enquiry types (Phase 123). "Request a Sign" files into the SAME list as the
// dedicated /request-a-sign form (backend dedupes), so nothing is doubled.
const ENQUIRY_TYPES = ['Suggestion', 'Request a Sign', 'Question', 'Volunteer', 'Media enquiry', 'Other'];
const SIGN = 'Request a Sign';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '', enquiry_types: [], sign_address: '', sign_size: 'either', sign_permission: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [settings, setSettings] = useState({});
  const [signOpts, setSignOpts] = useState({ sizes: [] });

  useEffect(() => {
    api.get('/settings').then((r) => setSettings(r.data || {})).catch(() => {});
    api.get('/sign-requests/options').then((r) => setSignOpts(r.data || { sizes: [] })).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const wantsSign = form.enquiry_types.includes(SIGN);
  const toggleType = (t, on) => set('enquiry_types', on ? [...form.enquiry_types, t] : form.enquiry_types.filter((x) => x !== t));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error('Name, email, and message are required'); return; }
    if (wantsSign) {
      if (form.sign_address.trim().length < 5) { toast.error('Please tell us the address where the sign should go.'); return; }
      if (!form.sign_permission) { toast.error('Please confirm you have permission to place a sign at that address.'); return; }
    }
    setBusy(true);
    try {
      const r = await api.post('/contact', form);
      setDone(r.data || { success: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Message failed to send. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader pageKey="contact" kicker="GET IN TOUCH" title="Contact Steve" subtitle="Questions, concerns, or ideas for Ward 8? Steve wants to hear from you directly." />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {done ? (
          <Card>
            <CardContent className="p-8 text-center" data-testid="contact-success">
              <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto mb-4" />
              <p className="font-serif font-black text-[#1a2744] text-2xl">Message sent</p>
              <p className="text-slate-600 mt-2">{done.message || 'Steve or a member of the campaign team will follow up with you.'}</p>
              {done.sign_reference && (
                <p className="mt-3 text-sm text-slate-700" data-testid="contact-sign-reference">Sign request reference: <strong>{done.sign_reference}</strong></p>
              )}
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Button asChild variant="outline" className="h-11 border-[#1a2744] text-[#1a2744] font-bold bg-white btn-press" data-testid="contact-success-home"><Link to="/home">Back to Home</Link></Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-5 sm:p-8">
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Your name <span className="text-red-600">*</span></label>
                  <Input required value={form.name} onChange={(e) => set('name', e.target.value)} className="h-12" autoComplete="name" data-testid="contact-name-input" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-bold text-[#1a2744] mb-2">Email <span className="text-red-600">*</span></label>
                    <Input type="email" required value={form.email} onChange={(e) => set('email', e.target.value)} className="h-12" autoComplete="email" data-testid="contact-email-input" />
                  </div>
                  <div>
                    <label className="block font-bold text-[#1a2744] mb-2">Phone</label>
                    <Input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} className="h-12" autoComplete="tel" data-testid="contact-phone-input" />
                  </div>
                </div>

                <fieldset data-testid="contact-enquiry-types">
                  <legend className="block font-bold text-[#1a2744] mb-2">What is this about? <span className="text-slate-500 font-normal text-sm">(tick any that apply)</span></legend>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {ENQUIRY_TYPES.map((t) => {
                      const id = `enq-${t.toLowerCase().replace(/[^a-z]+/g, '-')}`;
                      const on = form.enquiry_types.includes(t);
                      return (
                        <label key={t} htmlFor={id} className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 min-h-[44px] cursor-pointer ${on ? 'border-[#1a2744] bg-[#f3f7ff]' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                          <Checkbox id={id} checked={on} onCheckedChange={(v) => toggleType(t, !!v)} data-testid={`contact-type-${id.slice(4)}`} />
                          <span className="text-sm font-semibold text-[#1a2744] inline-flex items-center gap-1.5">{t === SIGN && <Signpost className="h-4 w-4 text-[#b91c1c]" />}{t}</span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>

                {wantsSign && (
                  <div className="rounded-xl border border-[#c7d7f5] bg-[#f3f7ff] p-4 space-y-4" data-testid="contact-sign-fields">
                    <p className="text-sm text-slate-700">Great. Tell us where the sign goes and we will add you to the volunteer placement list. Prefer the full form? <Link to="/request-a-sign" className="font-bold underline underline-offset-2 text-[#1a2744]" data-testid="contact-sign-full-form-link">Request a Sign</Link>.</p>
                    <div>
                      <Label htmlFor="contact-sign-address" className="font-bold text-[#1a2744]">Sign address <span className="text-red-600">*</span></Label>
                      <Input id="contact-sign-address" value={form.sign_address} onChange={(e) => set('sign_address', e.target.value)} placeholder="Street address and community" className="h-12 mt-1 bg-white" autoComplete="street-address" data-testid="contact-sign-address" />
                    </div>
                    {signOpts.sizes?.length > 0 && (
                      <div>
                        <p className="font-bold text-[#1a2744] text-sm mb-1.5">Sign size</p>
                        <RadioGroup value={form.sign_size} onValueChange={(v) => set('sign_size', v)} className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="contact-sign-sizes">
                          {signOpts.sizes.map((s) => (
                            <label key={s.key} htmlFor={`contact-size-${s.key}`} className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 min-h-[44px] cursor-pointer bg-white ${form.sign_size === s.key ? 'border-[#1a2744]' : 'border-slate-300'}`}>
                              <RadioGroupItem id={`contact-size-${s.key}`} value={s.key} data-testid={`contact-sign-size-${s.key}`} />
                              <span className="text-xs sm:text-sm font-semibold text-[#1a2744]">{s.label}</span>
                            </label>
                          ))}
                        </RadioGroup>
                      </div>
                    )}
                    <label htmlFor="contact-sign-permission" className="flex items-start gap-3 text-sm text-slate-800 cursor-pointer">
                      <Checkbox id="contact-sign-permission" checked={form.sign_permission} onCheckedChange={(v) => set('sign_permission', !!v)} className="mt-0.5 bg-white" data-testid="contact-sign-permission" />
                      <span>I confirm I own this property or have the owner's permission to place a campaign sign here. <span className="text-red-600">*</span></span>
                    </label>
                  </div>
                )}

                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Subject</label>
                  <Input value={form.subject} onChange={(e) => set('subject', e.target.value)} className="h-12" placeholder={form.enquiry_types.length ? form.enquiry_types.join(', ') : ''} data-testid="contact-subject-input" />
                </div>
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Message <span className="text-red-600">*</span></label>
                  <Textarea required value={form.message} onChange={(e) => set('message', e.target.value)} rows={6} data-testid="contact-message-input" />
                </div>
                <Button type="submit" disabled={busy} className="w-full py-3.5 h-13 bg-[#1a2744] hover:bg-[#243456] text-white font-bold text-base btn-press" data-testid="contact-submit-button">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Mail className="h-5 w-5 mr-2" />} Send Message
                </Button>
              </form>
              {settings.site?.contact_email && (
                <p className="text-sm text-slate-500 text-center mt-5">Or email directly: <a href={`mailto:${settings.site.contact_email}`} className="font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4">{settings.site.contact_email}</a></p>
              )}
            </CardContent>
          </Card>
        )}

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="contact-facebook-links">
          <Button asChild variant="outline" className="h-11 border-[#1a2744] text-[#1a2744] font-bold bg-white btn-press" data-testid="contact-facebook-page"><a href={FACEBOOK_PAGE_URL} target="_blank" rel="noopener noreferrer"><Facebook className="h-4 w-4 mr-2" /> Facebook page <ExternalLink className="h-3.5 w-3.5 ml-1.5" /></a></Button>
          <FacebookReviewsButton className="h-11 border-[#1a2744] text-[#1a2744] bg-white" testId="contact-facebook-reviews" />
        </div>
        <div className="mt-6"><FlyerDownloadSection compact loc="contact" /></div>
      </div>
    </div>
  );
}
